import type { ColorSoundClientConfig, SubjectTypeEnumType } from '@mark43/cad-api';
import type { SoundName } from '../sounds/play-sound';
import { toSoundAsset } from './configurable-sound-assets';
import { ALL_AGENCY_TYPES } from './color-sound-config-types';

/**
 * `matched: false` means nothing is configured, so the caller uses today's default.
 * `matched: true, value: null` means the agency explicitly configured "no color" / "no sound".
 */
export type ConfigLookup<T> = { matched: false } | { matched: true; value: T | null };

export interface PaletteTokens {
    baseToken: string;
    contentToken: string;
    // For renderers that can't read CSS variables, such as Esri map symbols.
    hex: string;
}

export interface ColorSoundConfigIndex {
    visualColors: Map<string, string | null>;
    /** The department has configured at least one event priority color. */
    hasPriorityColors: boolean;
    actionSounds: Map<string, string | null>;
    /** Built once per config, so a color resolves to the same object every render. */
    colorLookups: Map<string, ConfigLookup<PaletteTokens>>;
}

type SubjectId = number | null | undefined;
type AgencyTypeId = number | null | undefined;

const NOT_MATCHED = { matched: false } as const;
const MATCHED_NONE = { matched: true, value: null } as const;

function visualKey(subjectType: SubjectTypeEnumType, subjectId: number, agency: number) {
    return `${subjectType}:${subjectId}:${agency}`;
}

function actionKey(
    triggerType: string,
    subjectType: SubjectTypeEnumType,
    subjectId: number,
    agency: number
) {
    return `${triggerType}:${visualKey(subjectType, subjectId, agency)}`;
}

function setIfAbsent<V>(map: Map<string, V>, key: string, value: V) {
    if (!map.has(key)) {
        map.set(key, value);
    }
}

export function buildColorSoundConfigIndex(config: ColorSoundClientConfig): ColorSoundConfigIndex {
    const visualColors = new Map<string, string | null>();
    const actionSounds = new Map<string, string | null>();
    const colorLookups = new Map<string, ConfigLookup<PaletteTokens>>();

    for (const row of config.visualConfigs ?? []) {
        setIfAbsent(
            visualColors,
            visualKey(row.subjectType, row.subjectId, row.agencyTypeGlobalAttrId),
            row.color
        );
    }
    for (const row of config.actionConfigs ?? []) {
        // A NONE row is a catch-all, so its subjectId carries no meaning.
        const subjectId = row.subjectType === 'NONE' ? 0 : row.subjectId;
        setIfAbsent(
            actionSounds,
            actionKey(row.triggerType, row.subjectType, subjectId, row.agencyTypeGlobalAttrId),
            row.sound
        );
    }
    for (const entry of config.palette ?? []) {
        setIfAbsent(colorLookups, entry.color, {
            matched: true,
            value: {
                baseToken: entry.baseToken,
                contentToken: entry.contentToken,
                hex: entry.hex,
            },
        });
    }
    const hasPriorityColors = (config.visualConfigs ?? []).some(
        (row) => row.subjectType === 'EVENT_PRIORITY'
    );
    return { visualColors, hasPriorityColors, actionSounds, colorLookups };
}

function findFirst(map: Map<string, string | null>, keys: string[]): ConfigLookup<string> {
    for (const key of keys) {
        if (map.has(key)) {
            return { matched: true, value: map.get(key) ?? null };
        }
    }
    return NOT_MATCHED;
}

function subjectKeys(
    toKey: (subjectType: SubjectTypeEnumType, subjectId: number, agency: number) => string,
    subjectType: SubjectTypeEnumType,
    subjectId: SubjectId,
    agencyTypeGlobalAttrId: AgencyTypeId
): string[] {
    if (subjectId === null || subjectId === undefined) {
        return [];
    }
    const keys: string[] = [];
    if (agencyTypeGlobalAttrId) {
        keys.push(toKey(subjectType, subjectId, agencyTypeGlobalAttrId));
    }
    keys.push(toKey(subjectType, subjectId, ALL_AGENCY_TYPES));
    return keys;
}

/**
 * Order: (subject, agency) -> (subject, all agencies). Visual configs have no catch-all.
 * A configured color missing from the palette is treated as unconfigured.
 */
export function resolveVisualColor(
    index: ColorSoundConfigIndex,
    subjectType: SubjectTypeEnumType,
    subjectId: SubjectId,
    agencyTypeGlobalAttrId: AgencyTypeId
): ConfigLookup<PaletteTokens> {
    const lookup = findFirst(
        index.visualColors,
        subjectKeys(visualKey, subjectType, subjectId, agencyTypeGlobalAttrId)
    );
    if (!lookup.matched) {
        return NOT_MATCHED;
    }
    if (lookup.value === null) {
        return MATCHED_NONE;
    }
    return index.colorLookups.get(lookup.value) ?? NOT_MATCHED;
}

/**
 * Order: (subject, agency) -> (subject, all agencies) -> (NONE, agency) -> (NONE, all agencies).
 * Returns the bundled sound to play. A configured sound this build doesn't know is treated as
 * unconfigured.
 */
export function resolveActionSound(
    index: ColorSoundConfigIndex,
    triggerType: string,
    subjectType: SubjectTypeEnumType,
    subjectId: SubjectId,
    agencyTypeGlobalAttrId: AgencyTypeId
): ConfigLookup<SoundName> {
    const toKey = (type: SubjectTypeEnumType, id: number, agency: number) =>
        actionKey(triggerType, type, id, agency);
    const keys = [
        ...subjectKeys(toKey, subjectType, subjectId, agencyTypeGlobalAttrId),
        ...subjectKeys(toKey, 'NONE', 0, agencyTypeGlobalAttrId),
    ];
    for (const key of keys) {
        if (!index.actionSounds.has(key)) {
            continue;
        }
        const sound = index.actionSounds.get(key) ?? null;
        if (sound === null) {
            return MATCHED_NONE;
        }
        // A sound this build has no file for (the backend is ahead of the client) is skipped, so a
        // less specific row can still apply.
        const soundName = toSoundAsset(sound);
        if (soundName) {
            return { matched: true, value: soundName };
        }
    }
    return NOT_MATCHED;
}

/**
 * `fallback` is the palette's hex, used if this build's Arc doesn't define the token (the backend's
 * palette can be newer than the client), so a configured fill or flash never renders transparent.
 */
export function toCssVar(token: string, fallback?: string): string {
    return fallback ? `var(${token}, ${fallback})` : `var(${token})`;
}
